import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faPlus } from "@fortawesome/free-solid-svg-icons";
import { isApiConfigured } from "../auth-config";
import {
  deletePlaygroundProject,
  getPlaygroundProjects,
  postPlaygroundProject,
  putPlaygroundProject,
  type PlaygroundProjectSummary,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { useStudentCohortsOptional } from "../student/StudentCohortContext";

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function PlaygroundListPage() {
  const { profile, getBearerToken } = useAuth();
  const navigate = useNavigate();
  const bearer = getBearerToken();
  const isTeacher = profile?.role === "teacher";
  const { hasCohorts } = useStudentCohortsOptional();
  const canCreatePlayground = isTeacher || hasCohorts;

  const [projects, setProjects] = useState<PlaygroundProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const createDialogRef = useRef<HTMLDialogElement>(null);

  const closeCreate = useCallback(() => {
    setCreateOpen(false);
    setNewTitle("");
  }, []);

  useEffect(() => {
    const el = createDialogRef.current;
    if (!el) return;
    if (createOpen && !el.open) el.showModal();
    else if (!createOpen && el.open) el.close();
  }, [createOpen]);

  const reload = useCallback(async () => {
    if (!bearer || !isApiConfigured()) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const data = await getPlaygroundProjects(bearer);
      setProjects(data.projects);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load playground projects");
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onCreate = useCallback(async () => {
    const title = newTitle.trim();
    if (!bearer || !title) return;
    setBusy(true);
    setError(null);
    try {
      const data = await postPlaygroundProject(bearer, { title });
      closeCreate();
      navigate(`/playground/${data.project.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create the playground");
      setBusy(false);
    }
  }, [bearer, closeCreate, navigate, newTitle]);

  const onRename = useCallback(
    async (project: PlaygroundProjectSummary) => {
      if (!bearer) return;
      const next = window.prompt("Rename playground", project.title)?.trim();
      if (!next || next === project.title) return;
      setError(null);
      try {
        await putPlaygroundProject(bearer, project.id, { title: next });
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not rename the playground");
      }
    },
    [bearer, reload]
  );

  const onDelete = useCallback(
    async (project: PlaygroundProjectSummary) => {
      if (!bearer) return;
      if (!window.confirm(`Delete "${project.title}" and all of its files? This cannot be undone.`)) return;
      setError(null);
      try {
        await deletePlaygroundProject(bearer, project.id);
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not delete the playground");
      }
    },
    [bearer, reload]
  );

  const panelClass = isTeacher ? "teacher-panel" : "panel";
  const panelTitleClass = isTeacher ? "teacher-panel-title" : "panel-title";

  const body = (
    <>
      {!isApiConfigured() ? (
        <p className="muted">
          Configure <code className="inline-code">VITE_API_BASE_URL</code> to use the playground.
        </p>
      ) : null}

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      {!isTeacher ? (
        <>
          <section className={panelClass}>
            <h2 className={panelTitleClass}>Quick script</h2>
            <div className="quick-script-cta">
              <p className="muted small quick-script-cta-text">
                Need a scratchpad? Open a quick script for instant code and a console. No setup, works great on
                your phone, and you can save it as a playground anytime.
              </p>
              <Link to="/playground/quick" className="btn primary" style={{ textDecoration: "none" }}>
                Open a quick script
              </Link>
            </div>
          </section>

          <section className={panelClass}>
            <h2 className={panelTitleClass}>Start a new playground</h2>
            {canCreatePlayground ? (
              <>
                <form
                  className="actions inline"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void onCreate();
                  }}
                >
                  <input
                    type="text"
                    className="input"
                    placeholder="Playground name"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    aria-label="New playground name"
                  />
                  <button type="submit" className="btn primary" disabled={busy || !newTitle.trim() || !bearer}>
                    {busy ? "Creating…" : "Create playground"}
                  </button>
                </form>
                <p className="muted">
                  Every playground starts with a main.py. Add more files inside the editor.
                </p>
              </>
            ) : (
              <p className="muted">
                Join a cohort before you can create playgrounds. Ask your teacher for a join code, then{" "}
                <Link to="/join">join a cohort</Link>. You can still open and update playgrounds you created
                earlier.
              </p>
            )}
          </section>
        </>
      ) : null}

      <section className={panelClass}>
        <h2 className={panelTitleClass}>Your playgrounds</h2>
        {loading ? (
          <p className="muted">Loading playgrounds…</p>
        ) : projects.length === 0 ? (
          <p className="muted">
            {canCreatePlayground
              ? "No playgrounds yet. Create your first one from the header above."
              : "No playgrounds yet. Join a cohort to start creating."}
          </p>
        ) : (
          <div className={isTeacher ? "teacher-table-wrap" : undefined}>
            <table className={`teacher-table${isTeacher ? "" : " student-responsive-table"}`}>
              <thead>
                <tr>
                  <th>Playground</th>
                  <th>Files</th>
                  <th>Last updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id}>
                    <td data-label="Playground">
                      <strong>{p.title}</strong>
                    </td>
                    <td data-label="Files">{p.fileCount}</td>
                    <td data-label="Last updated">{formatTimestamp(p.updatedAt)}</td>
                    <td className="teacher-table-actions">
                      <Link to={`/playground/${p.id}`} className="btn primary tight" style={{ textDecoration: "none" }}>
                        Open
                      </Link>
                      <button type="button" className="btn secondary tight" onClick={() => void onRename(p)}>
                        Rename
                      </button>
                      <button type="button" className="btn secondary tight" onClick={() => void onDelete(p)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );

  if (isTeacher) {
    return (
      <div className="teacher-page submissions-dashboard">
        <header className="submissions-hero overview-hero">
          <div className="submissions-hero-text">
            <p className="eyebrow">Teacher · Playground</p>
            <h1 className="teacher-page-title">My playgrounds</h1>
            <p className="teacher-page-lede">
              Multi-file Python playgrounds that run in your browser and save to your account.
            </p>
          </div>
          <div className="overview-hero-actions">
            <Link to="/playground/quick" className="btn secondary">
              <FontAwesomeIcon icon={faBolt} /> Quick script
            </Link>
            <button
              type="button"
              className="btn primary"
              disabled={!canCreatePlayground || !bearer}
              onClick={() => setCreateOpen(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> New playground
            </button>
          </div>
        </header>
        {body}
        {createOpen ? (
          <dialog
            ref={createDialogRef}
            className="teacher-modal"
            aria-labelledby="create-playground-title"
            onClose={closeCreate}
            onClick={(ev) => {
              if (ev.target === createDialogRef.current && !busy) closeCreate();
            }}
          >
            <div className="teacher-modal-card" role="document">
              <h2 id="create-playground-title" className="teacher-modal-title">
                New playground
              </h2>
              <p className="muted small">
                Every playground starts with a main.py. Add more files inside the editor.
              </p>
              <form
                className="stack-form tight"
                onSubmit={(e) => {
                  e.preventDefault();
                  void onCreate();
                }}
              >
                <label className="field">
                  <span className="field-label">Playground name</span>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g. Totes McGoats"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    disabled={busy}
                    autoFocus
                    required
                  />
                </label>
                <div className="teacher-modal-actions">
                  <button type="button" className="btn secondary" disabled={busy} onClick={closeCreate}>
                    Cancel
                  </button>
                  <button type="submit" className="btn primary" disabled={busy || !newTitle.trim()}>
                    {busy ? "Creating…" : "Create playground"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        ) : null}
      </div>
    );
  }

  return (
    <main className="page page-align-start student-page-inner">
      <div className="card card-wide">
        <header className="row-header">
          <div>
            <p className="eyebrow">Playground</p>
            <h1>My playgrounds</h1>
            <p className="lede">
              Multi-file Python playgrounds that run in your browser and save to your account.
            </p>
          </div>
        </header>
        {body}
      </div>
    </main>
  );
}
